<script setup lang="ts">
// M1 static inference harness (?harness=1): drop a photo -> detector finds plates,
// OCR reads the crop. Also exercises the guide-box (detector-free) path.
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { MODELS } from '../../engine/pipeline/modelManifest';
import type { PipelineResult, WorkerToMain } from '../../engine/types';

const canvasRef = ref<HTMLCanvasElement | null>(null);
const status = ref('loading models…');
const results = ref<string[]>([]);
const useGuidePath = ref(false);

let worker: Worker | null = null;
let ready = false;
let lastBitmapSrc: ImageBitmap | null = null;

onMounted(() => {
  worker = new Worker(new URL('../../engine/pipeline/worker.ts', import.meta.url), {
    type: 'module',
  });
  worker.onmessage = (ev: MessageEvent<WorkerToMain | { t: 'result' } & PipelineResult>) => {
    const msg = ev.data;
    if (msg.t === 'ready') {
      ready = true;
      status.value = `ready — ep=${msg.ep}`;
    } else if (msg.t === 'progress') {
      status.value = `downloading models… ${Math.round((msg.loaded / (msg.total || 1)) * 100)}%`;
    } else if (msg.t === 'error') {
      status.value = `error: ${msg.message}`;
    } else if (msg.t === 'result') {
      renderResult(msg);
    }
  };
  const forceEp = new URLSearchParams(location.search).get('ep');
  worker.postMessage({
    t: 'init',
    detectorUrl: MODELS.detector.url,
    ocrUrl: MODELS.ocr.url,
    ...(forceEp === 'wasm' || forceEp === 'webgpu' ? { forceEp } : {}),
  });
});
onBeforeUnmount(() => {
  worker?.terminate();
  lastBitmapSrc?.close();
});

async function onFile(e: Event): Promise<void> {
  const file = (e.target as HTMLInputElement).files?.[0];
  if (!file || !ready || !worker) return;
  results.value = [];
  const bitmap = await createImageBitmap(file);
  lastBitmapSrc?.close();
  lastBitmapSrc = await createImageBitmap(file); // keep one copy for drawing
  const canvas = canvasRef.value;
  if (canvas) {
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    canvas.getContext('2d')?.drawImage(bitmap, 0, 0);
  }
  status.value = 'running…';
  worker.postMessage(
    {
      t: 'frame',
      bitmap,
      mode: useGuidePath.value ? 'guide' : 'auto',
      rectify: !useGuidePath.value,
      frameId: 1,
    },
    [bitmap],
  );
}

function renderResult(r: PipelineResult): void {
  status.value = `done in ${r.timings.total.toFixed(0)} ms (det ${r.timings.detect.toFixed(0)} / rect ${r.timings.rectify.toFixed(0)} / ocr ${r.timings.ocr.toFixed(0)}) ep=${r.ep}`;
  const ctx = canvasRef.value?.getContext('2d');
  if (ctx && lastBitmapSrc) {
    ctx.drawImage(lastBitmapSrc, 0, 0);
    const lw = Math.max(2, lastBitmapSrc.width / 300);
    if (r.detection) {
      ctx.strokeStyle = '#27e07d';
      ctx.lineWidth = lw;
      ctx.strokeRect(r.detection.box.x, r.detection.box.y, r.detection.box.w, r.detection.box.h);
    }
    if (r.quad) {
      ctx.strokeStyle = '#4aa8ff';
      ctx.lineWidth = lw;
      ctx.beginPath();
      ctx.moveTo(r.quad[0].x, r.quad[0].y);
      for (let i = 1; i <= 4; i++) ctx.lineTo(r.quad[i % 4]?.x ?? 0, r.quad[i % 4]?.y ?? 0);
      ctx.stroke();
    }
  }
  if (r.read) {
    const conf =
      r.read.charConfs.length > 0
        ? r.read.charConfs.reduce((a, b) => a + b, 0) / r.read.charConfs.length
        : 0;
    const box = r.detection
      ? ` box[${[r.detection.box.x, r.detection.box.y, r.detection.box.w, r.detection.box.h].map((v) => v.toFixed(0)).join(',')}]`
      : '';
    results.value.push(
      `${r.read.raw}  (conf ${(conf * 100).toFixed(1)}%${r.read.region ? `, ${r.read.region.name}` : ''}${r.detection ? `, det ${(r.detection.score * 100).toFixed(0)}%` : ''})${box}`,
    );
  } else {
    results.value.push(r.detection ? '(detected, no read)' : '(no plate found)');
  }
}
</script>

<template>
  <div class="harness">
    <h1 class="brand">AutoSpy harness</h1>
    <p class="muted">{{ status }}</p>
    <div class="harness-controls">
      <input type="file" accept="image/*" @change="onFile" />
      <label class="muted">
        <input v-model="useGuidePath" type="checkbox" />
        guide-box path (OCR whole image, no detector)
      </label>
    </div>
    <ul>
      <li v-for="(r, i) in results" :key="i" class="plate-small">{{ r }}</li>
    </ul>
    <canvas ref="canvasRef" class="harness-canvas"></canvas>
  </div>
</template>
